import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Plus, Pencil, Trash2, X, Check, Loader2 } from "lucide-react";

const ICON_OPTIONS = ["stethoscope", "siren", "test-tube", "syringe", "calendar-check"];
const COLOR_OPTIONS = ["#2563EB", "#DC2626", "#7C3AED", "#16A34A", "#D97706", "#0891B2", "#DB2777"];

export function ServicesPanel({ isDemo }) {
  const { t } = useI18n();
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/services");
    setServices(data.services);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ name_fr: "", name_en: "", prefix: "", priority: 10, color: COLOR_OPTIONS[0], icon: ICON_OPTIONS[0], active: true });
    setErr("");
  };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...s }); setErr(""); };
  const cancel = () => { setEditing(null); setForm(null); setErr(""); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing === "new") {
        await api.post("/admin/services", form);
      } else {
        await api.patch(`/admin/services/${editing}`, form);
      }
      await load();
      cancel();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    setBusy(true); setErr("");
    try { await api.delete(`/admin/services/${id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <div className="p-5 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="font-heading text-xl font-semibold">{t("manage_services")}</h3>
          {isDemo && <div className="text-xs text-amber-700 mt-1">{t("demo_action_disabled")}</div>}
        </div>
        <button onClick={startNew} disabled={isDemo} data-testid="admin-add-service" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {t("add_service")}
        </button>
      </div>
      {err && <div className="mx-5 mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="admin-services-error">{err}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.15em] text-slate-500 bg-slate-50">
            <tr>
              <th className="px-5 py-3">{t("prefix")}</th>
              <th className="px-5 py-3">{t("name_fr")} / {t("name_en")}</th>
              <th className="px-5 py-3">{t("priority")}</th>
              <th className="px-5 py-3">{t("color")}</th>
              <th className="px-5 py-3">{t("active")}</th>
              <th className="px-5 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="admin-services-table">
            {editing === "new" && <EditRow form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew />}
            {services.map((s) => editing === s.id ? (
              <EditRow key={s.id} form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} />
            ) : (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-semibold">{s.prefix}</td>
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-900">{s.name_fr}</div>
                  <div className="text-xs text-slate-500">{s.name_en}</div>
                </td>
                <td className="px-5 py-3">{s.priority}</td>
                <td className="px-5 py-3">
                  <div className="w-5 h-5 rounded-full" style={{ backgroundColor: s.color }} />
                </td>
                <td className="px-5 py-3">{s.active ? "✓" : "—"}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1">
                    <button disabled={isDemo} onClick={() => startEdit(s)} data-testid={`admin-service-edit-${s.prefix}`} className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-40"><Pencil className="w-4 h-4" /></button>
                    <button disabled={isDemo} onClick={() => remove(s.id)} data-testid={`admin-service-delete-${s.prefix}`} className="p-2 rounded-full hover:bg-rose-50 text-rose-700 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRow({ form, setForm, onSave, onCancel, busy, isNew }) {
  return (
    <tr className="bg-blue-50/40">
      <td className="px-5 py-3">
        <input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase().slice(0, 3) })} data-testid="admin-service-input-prefix" className="w-16 px-2 py-1 border border-slate-300 rounded-lg font-mono uppercase" />
      </td>
      <td className="px-5 py-3 space-y-1">
        <input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="admin-service-input-namefr" placeholder="FR" className="w-full px-2 py-1 border border-slate-300 rounded-lg" />
        <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="admin-service-input-nameen" placeholder="EN" className="w-full px-2 py-1 border border-slate-300 rounded-lg" />
      </td>
      <td className="px-5 py-3">
        <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "0", 10) })} data-testid="admin-service-input-priority" className="w-20 px-2 py-1 border border-slate-300 rounded-lg" />
      </td>
      <td className="px-5 py-3">
        <div className="flex flex-wrap gap-1">
          {COLOR_OPTIONS.map((c) => (
            <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`w-6 h-6 rounded-full border-2 ${form.color === c ? "border-slate-900" : "border-transparent"}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </td>
      <td className="px-5 py-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          {form.active ? "✓" : "—"}
        </label>
      </td>
      <td className="px-5 py-3">
        <div className="flex gap-1">
          <button onClick={onSave} disabled={busy} data-testid={isNew ? "admin-service-save-new" : "admin-service-save-edit"} className="p-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button onClick={onCancel} className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  );
}

export function CountersPanel({ isDemo }) {
  const { t } = useI18n();
  const [counters, setCounters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/counters"); setCounters(data.counters); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing === "new") await api.post("/admin/counters", { name });
      else await api.patch(`/admin/counters/${editing}`, { name });
      await load();
      setEditing(null); setName("");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/admin/counters/${id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <div className="p-5 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="font-heading text-xl font-semibold">{t("manage_counters")}</h3>
          {isDemo && <div className="text-xs text-amber-700 mt-1">{t("demo_action_disabled")}</div>}
        </div>
        <button onClick={() => { setEditing("new"); setName(""); setErr(""); }} disabled={isDemo} data-testid="admin-add-counter" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {t("add_counter")}
        </button>
      </div>
      {err && <div className="mx-5 mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}
      <div className="p-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-counters-grid">
        {editing === "new" && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Salle 4" data-testid="admin-counter-input-new" className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
            <div className="mt-2 flex gap-2">
              <button onClick={save} disabled={busy || !name} data-testid="admin-counter-save-new" className="flex-1 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{busy ? "…" : t("save")}</button>
              <button onClick={() => { setEditing(null); setName(""); }} className="flex-1 py-2 rounded-full border border-slate-200 hover:bg-slate-50">{t("cancel")}</button>
            </div>
          </div>
        )}
        {counters.map((c) => editing === c.id ? (
          <div key={c.id} className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
            <div className="mt-2 flex gap-2">
              <button onClick={save} className="flex-1 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">{t("save")}</button>
              <button onClick={() => { setEditing(null); setName(""); }} className="flex-1 py-2 rounded-full border border-slate-200 hover:bg-slate-50">{t("cancel")}</button>
            </div>
          </div>
        ) : (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between">
            <div className="font-heading text-lg font-semibold">{c.name}</div>
            <div className="flex gap-1">
              <button disabled={isDemo} onClick={() => { setEditing(c.id); setName(c.name); }} className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-40"><Pencil className="w-4 h-4" /></button>
              <button disabled={isDemo} onClick={() => remove(c.id)} data-testid={`admin-counter-delete-${c.name.replace(/\s/g, '-')}`} className="p-2 rounded-full hover:bg-rose-50 text-rose-700 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UsersPanel({ isDemo, currentUserId }) {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "agent", password: "" });
  const [tempCred, setTempCred] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data.users); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true); setErr(""); setTempCred(null);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      const { data } = await api.post("/admin/users", body);
      await load();
      if (data.temp_password) setTempCred({ email: data.email, password: data.temp_password });
      setAdding(false);
      setForm({ email: "", name: "", role: "agent", password: "" });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/admin/users/${id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  const togglePermission = async (u, perm) => {
    if (isDemo || u.is_demo) return;
    const current = Array.isArray(u.permissions) ? u.permissions : [];
    const next = current.includes(perm) ? current.filter((p) => p !== perm) : [...current, perm];
    // Optimistic update
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, permissions: next } : x)));
    try {
      await api.patch(`/admin/users/${u.id}`, { permissions: next });
    } catch (e) {
      setErr(formatApiError(e));
      await load();
    }
  };

  const PERMS = ["dashboard", "staff", "kiosk", "display"];

  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <div className="p-5 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="font-heading text-xl font-semibold">{t("manage_users")}</h3>
          {isDemo && <div className="text-xs text-amber-700 mt-1">{t("demo_action_disabled")}</div>}
        </div>
        <button onClick={() => setAdding(true)} disabled={isDemo} data-testid="admin-add-user" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {t("add_user")}
        </button>
      </div>
      {err && <div className="mx-5 mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="admin-users-error">{err}</div>}
      {tempCred && (
        <div className="mx-5 mt-4 text-sm bg-emerald-50 border border-emerald-100 text-emerald-900 rounded-xl p-3" data-testid="admin-user-temp-cred">
          <div className="font-medium">{t("temp_password")}</div>
          <div className="font-mono">{tempCred.email} / <span className="font-bold">{tempCred.password}</span></div>
        </div>
      )}
      {adding && (
        <div className="mx-5 mt-4 p-4 rounded-xl border border-blue-200 bg-blue-50 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-user-input-email" className="px-3 py-2 border border-slate-300 rounded-lg" />
          <input placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-input-name" className="px-3 py-2 border border-slate-300 rounded-lg" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-user-input-role" className="px-3 py-2 border border-slate-300 rounded-lg">
            <option value="agent">agent</option>
            <option value="tenant_admin">tenant_admin</option>
          </select>
          <input placeholder={t("password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-user-input-password" className="px-3 py-2 border border-slate-300 rounded-lg" />
          <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
            <button onClick={submit} disabled={busy || !form.email || !form.name} data-testid="admin-user-save-new" className="px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">{busy ? "…" : t("save")}</button>
            <button onClick={() => { setAdding(false); setForm({ email: "", name: "", role: "agent", password: "" }); }} className="px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50">{t("cancel")}</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.15em] text-slate-500 bg-slate-50">
            <tr>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">{t("name")}</th>
              <th className="px-5 py-3">{t("role")}</th>
              <th className="px-5 py-3">{t("permissions")}</th>
              <th className="px-5 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="admin-users-table">
            {users.map((u) => {
              const userPerms = Array.isArray(u.permissions) ? u.permissions : [];
              const lockedRow = isDemo || u.is_demo;
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono text-slate-700">{u.email}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{u.role}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1.5" data-testid={`admin-user-perms-${u.email.split("@")[0]}`}>
                      {PERMS.map((p) => {
                        const on = userPerms.includes(p);
                        return (
                          <button
                            key={p}
                            type="button"
                            disabled={lockedRow}
                            onClick={() => togglePermission(u, p)}
                            data-testid={`admin-user-perm-${u.email.split("@")[0]}-${p}`}
                            title={p}
                            className={`px-2 py-1 rounded-full text-xs font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                              on
                                ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                            }`}
                          >
                            {t(`permission_${p}`)}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <button disabled={lockedRow || u.id === currentUserId} onClick={() => remove(u.id)} data-testid={`admin-user-delete-${u.email.split("@")[0]}`} className="p-2 rounded-full hover:bg-rose-50 text-rose-700 disabled:opacity-30">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
